import { Dexie, Table } from "dexie";
import {
  Flow,
  Collaborators,
  Feedback,
  Logs,
  Unlockedfeatures,
  User,
  CustomProperty,
  Template,
  //TemplateSubscribers
} from "../types/types";
import { nanoid } from "nanoid";
import { TreeCheckboxSelectionKeys } from "primereact/tree";

// 🔹 Initialize the database
class FocusMapDB extends Dexie {
  flows!: Table<Flow, string>;
  collaborators!: Table<Collaborators, string>;
  feedback!: Table<Feedback, string>;
  logs!: Table<Logs, string>;
  unlockedfeatures!: Table<Unlockedfeatures, string>;
  users!: Table<User, string>;
  flowProps!: Table<CustomProperty, string>;
  template!: Table<Template, string>;
  // templateSubscribers!: Table<TemplateSubscribers, string>;

  constructor() {
    super("FocusMapDB");
    this.version(1).stores({
      flows: "id, name, email, timestamp, favouite", // Define indexes
      collaborators: "id, collaborators, owner",
      unlockedFeature: "id, startDate, endDate",
      users: "id",
      feedback: "id",
      logs: "id",
      flowProps: "id",
    });
  }
}

// 🔹 Export an instance of the database
export const db = new FocusMapDB();

// Function to load flow data from Dexie
export const loadFlowData = async (flowId: string) => {
  try {
    return await db.flows.get(flowId);
  } catch (e) {
    console.error("Error loading flow data: ", e);
    return null;
  }
};

// Function to save flow data to Dexie
export const saveFlowData = async (flowId: string, data: any) => {
  try {
    await db.flows.put({ id: flowId, ...data });
    return { success: true, error: null };
  } catch (error) {
    console.error("Error saving flow data: " + error);
    return { success: false, error: error };
  }
};

// Function to load flow data from Dexie
export const loadFlowProps = async (flowId: string) => {
  try {
    return await db.flowProps.get(flowId);
  } catch (e) {
    console.error("Error loading flow data: ", e);
    return null;
  }
};

// Function to save flow props to Dexie
export const saveFlowProps = async (flowId: string, data: any) => {
  try {
    await db.flowProps.put({ id: flowId, ...data });
    return { success: true, error: null };
  } catch (error) {
    console.error("Error saving flow props: " + error);
    return { success: false, error: error };
  }
};

// Function to add a collaborator to Dexie
export const addCollaborator = async (
  id: string,
  owner: string,
  collaborator: string
) => {
  try {
    const flowCollaborators = await db.collaborators.get(id);
    if (flowCollaborators) {
      const collaborators = flowCollaborators.collaborators || [];
      collaborators.push(collaborator);
      await db.collaborators.put({
        ...flowCollaborators,
        collaborators,
        modifiedAt: new Date().toISOString(),
      });
    } else {
      await db.collaborators.put({
        id: id,
        owner: owner,
        collaborators: [collaborator],
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error("Error adding collaborator: ", e);
  }
};

// Function to remove a collaborator from Dexie
export const removeCollaborator = async (flowId: string, email: string) => {
  try {
    const flowCollaborators = await db.collaborators.get(flowId);
    if (flowCollaborators) {
      const collaborators = flowCollaborators.collaborators.filter(
        (collaborator: string) => collaborator !== email
      );
      await db.collaborators.put({
        ...flowCollaborators,
        collaborators,
        modifiedAt: new Date().toISOString(),
      });
    }
  } catch (e) {
    console.error("Error removing collaborator: ", e);
  }
};

// Function to load collaborators from Dexie
export const loadCollaborators = async (flowId: string) => {
  try {
    const flow = await db.collaborators.get(flowId);
    return flow ? flow.collaborators || [] : [];
  } catch (e) {
    console.error("Error loading collaborators: ", e);
    return [];
  }
};

// Function to fetch flows from Dexie
export const fetchFlows = async (email: string) => {
  try {
    const ownedFlows = await db.flows.where("email").equals(email).toArray();
    const collaboratorFlows = await db.collaborators
      .where("collaborators")
      .equals(email)
      .toArray();
    return [...ownedFlows, ...collaboratorFlows];
  } catch (e) {
    console.error("Error fetching flows: ", e);
    return [];
  }
};

// Function to get user plan from Dexie
export const getUserPlan = async (user: any) => {
  try {
    const userObject = await db.users.get(user.uid);
    return userObject ? userObject.plan.toLowerCase() : null;
  } catch (e) {
    console.error("Error getting user plan: ", e);
    return null;
  }
};

// Function to add feedback to Dexie
export const addFeedback = async (email: string, feedback: string) => {
  try {
    await db.feedback.add({
      id: nanoid(),
      email,
      feedback,
      timestamp: new Date().toISOString(),
    });
  } catch (e) {
    console.error("Error adding feedback: ", e);
  }
};

// Function to check if a feature is unlocked in Dexie
export const unlockedFeature = async (featureId: string): Promise<boolean> => {
  try {
    const feature = await db.unlockedfeatures.get(featureId);
    if (feature) {
      const { startDate, endDate } = feature;
      const now = new Date();
      return now >= new Date(startDate) && now <= new Date(endDate);
    }
    return false;
  } catch (e) {
    console.error("Error checking unlocked feature: ", e);
    return false;
  }
};

// Function to get unlocked features from Dexie
export const getUnlockedFeatures = async (): Promise<string[]> => {
  try {
    const now = new Date();
    const unlockedFeatures = await db.unlockedfeatures
      .filter((feature) => {
        const { startDate, endDate } = feature;
        return now >= new Date(startDate) && now <= new Date(endDate);
      })
      .toArray();
    return unlockedFeatures.map((feature) => feature.id);
  } catch (e) {
    console.error("Error getting unlocked features: ", e);
    return [];
  }
};

// Function to update user's showWelcome in Dexie
export const updateUserShowWelcome = async (
  userId: string,
  showWelcome: boolean
) => {
  try {
    await db.users.update(userId, { showWelcome });
  } catch (e) {
    console.error("Error updating showWelcome: ", e);
  }
};

export const updateUserInsights = async (
  userId: string,
  insights: TreeCheckboxSelectionKeys
) => {
  try {
    await db.users.update(userId, { insights });
  } catch (e) {
    console.error("Error updating user insights: ", e);
  }
};

export const updateUserSettings = async (
  userId: string,
  settings: {
    presentWithShowHideButtons: boolean;
    currency: string;
    showWhyTag?: boolean;
    showStartupDialog?: boolean;
    showAIPromptOnNewMap?: boolean;
    aiModel?: string;
  }
) => {
  try {
    await db.users.update(userId, { settings });
  } catch (e) {
    console.error("Error updating user settings: ", e);
  }
};

// Function to update user's showWelcome in Dexie
export const updateUserUseLocalData = async (
  userId: string,
  useLocalData: boolean
) => {
  try {
    await db.users.update(userId, { useLocalData });
  } catch (e) {
    console.error("Error updating useLocalData: ", e);
  }
};

// Function to get user document from Dexie
export const getUserDoc = async (userId: string) => {
  try {
    return await db.users.get(userId);
  } catch (error) {
    console.error("Error getting user document: ", error);
    return null;
  }
};

// Function to check user's plan and flow limit in Dexie
export const checkUserPlanAndFlowLimit = async (
  userId: string,
  email: string
) => {
  try {
    const user = await db.users.get(userId);
    if (user && user.plan === "free") {
      const flows = await db.flows.where("email").equals(email).toArray();
      if (flows.length >= 2) {
        return { limitReached: true };
      }
    }
    return { limitReached: false };
  } catch (error) {
    console.error("Error checking user plan and flow limit:", error);
    return { limitReached: false, error };
  }
};

// Function to get user data by email from Dexie
export const getUserDataByEmail = async (email: string) => {
  try {
    const user = await db.flows.where("email").equals(email).first();
    return user || null;
  } catch (error) {
    console.error("Error getting user data by email:", error);
    return null;
  }
};

// Function to update or create user document in Dexie
export const updateOrCreateUserDoc = async (
  user: any,
  email?: string
): Promise<boolean> => {
  try {
    const existingUser = await db.users.get(user.uid);
    if (existingUser) {
      await db.users.update(user.uid, { modifiedAt: new Date().toISOString() });
      return existingUser.useLocalData;
    } else {
      await db.users.add({
        id: user.uid,
        email: email || user.email,
        name: user.name || "",
        plan: "free",
        showWelcome: true,
        useLocalData: false,
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        insights: {
          keys: [],
        } as TreeCheckboxSelectionKeys,
        settings: {
          presentWithShowHideButtons: false,
          currency: "USD",
        },
      });
      return false;
    }
  } catch (error) {
    console.error("Error updating or creating user document:", error);
    return false;
  }
};

export const updateUseLocalData = async (
  user: any,
  useLocalData: boolean
): Promise<boolean> => {
  try {
    await db.users.update(user.uid, { useLocalData: useLocalData });
    return true;
  } catch (error) {
    console.error("Error updating useLocalData:", error);
    return false;
  }
};

export const deleteFlowData = async (flowId: string) => {
  try {
    await db.flows.delete(flowId);
    console.log(`Flow with id ${flowId} deleted successfully.`);
  } catch (error) {
    console.error("Error deleting flow data: ", error);
  }
};

export const loadTemplate = async (flowId: string) => {
  try {
    return await db.template.get(flowId);
  } catch (e) {
    console.error("Error loading template data: ", e);
    return null;
  }
};

export const loadMyTemplates = async (email: string): Promise<Template[]> => {
  try {
    return await db.template.where("email").equals(email).toArray();
  } catch (e) {
    console.error("Error loading my templates: ", e);
    return [];
  }
};

export const saveTemplate = async (flowId: string, data: any) => {
  try {
    await db.template.put({ id: flowId, ...data });
    return { success: true, error: null };
  } catch (error) {
    console.error("Error saving template data: " + error);
    return { success: false, error: error };
  }
};

export const deleteTemplate = async (id: string) => {
  try {
    await db.template.delete(id);
    console.log(`Template with id ${id} deleted successfully.`);
  } catch (error) {
    console.error("Error deleting template data: ", error);
  }
};

export function dexie(
  email: string
):
  | { success: boolean; error: any }
  | PromiseLike<{ success: boolean; error: any }> {
  throw new Error("Function not implemented.");
}
